import { useCallback, useEffect, useState } from 'react';
import {
  updateVendorCategory,
  fetchVendorRelation,
  fetchVendorProductCategories,
  type VendorRelation,
  type VendorCategoryRef,
} from '@/lib/marketplaceCatalog';
import { fetchCommerceCategories, type CommerceCategory } from '@/lib/marketplaceCategories';

// ─────────────────────────────────────────────────────────────
// Carte « Ma catégorie » du vendeur.
// Le vendeur choisit sa catégorie principale (affichée sur sa boutique
// publique). Ses produits/services restent classables dans d'autres
// catégories, et le vendeur apparaît alors dans chacune d'elles.
// ─────────────────────────────────────────────────────────────

interface VendorCategoryCardProps {
  marketplaceId?: number;
  userId: number;
  relation: VendorRelation | null;
  onChanged: (rel: VendorRelation) => void;
}

export default function VendorCategoryCard({ marketplaceId, userId, relation, onChanged }: VendorCategoryCardProps) {
  const [categories, setCategories] = useState<CommerceCategory[]>([]);
  const [productCats, setProductCats] = useState<VendorCategoryRef[]>([]);
  const [selected, setSelected] = useState<string>(relation?.categorieId != null ? String(relation.categorieId) : '');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setSelected(relation?.categorieId != null ? String(relation.categorieId) : '');
  }, [relation?.categorieId]);

  const load = useCallback(async () => {
    if (!marketplaceId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [cats, prodCats] = await Promise.all([
        fetchCommerceCategories(marketplaceId),
        fetchVendorProductCategories(userId),
      ]);
      setCategories(cats.filter((c) => c.afficher === 1));
      setProductCats(prodCats);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [marketplaceId, userId]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    if (!relation?.relationId) {
      setError('Votre compte vendeur n\u2019est pas encore rattaché à la marketplace.');
      return;
    }
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      const id = selected ? Number(selected) : null;
      const nom = id != null ? categories.find((c) => c.id === id)?.nom ?? null : null;
      await updateVendorCategory(relation.relationId, id, nom);
      const refreshed = await fetchVendorRelation(marketplaceId ?? 0, userId);
      if (refreshed) onChanged(refreshed);
      else onChanged({ ...relation, categorieId: id, categorieNom: nom });
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur lors de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
      </div>
    );
  }

  return (
    <div className="space-y-5 max-w-2xl">
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-11 h-11 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center flex-shrink-0">
            <i className="ri-price-tag-3-line text-xl"></i>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-foreground-950">Catégorie principale</h3>
            <p className="text-xs text-foreground-500">Elle s&apos;affiche sur votre boutique publique.</p>
          </div>
        </div>

        <label className="block text-xs font-semibold text-foreground-600 mb-1.5">
          Choisissez votre catégorie
        </label>
        <select
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          className="w-full px-3.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 cursor-pointer"
        >
          <option value="">Aucune catégorie</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>{c.nom}</option>
          ))}
        </select>

        {categories.length === 0 && (
          <p className="mt-2 text-xs text-foreground-400">
            La marketplace n&apos;a pas encore créé de catégories.
          </p>
        )}

        {error && (
          <div className="mt-3 px-3.5 py-2.5 rounded-lg bg-red-50 text-red-600 text-xs flex items-center gap-2">
            <i className="ri-error-warning-line"></i>
            {error}
          </div>
        )}

        <div className="mt-4 flex items-center gap-3">
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 whitespace-nowrap"
          >
            {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}
            Enregistrer
          </button>
          {saved && (
            <span className="text-xs text-accent-700 flex items-center gap-1">
              <i className="ri-check-line"></i>
              Catégorie enregistrée
            </span>
          )}
        </div>
      </div>

      <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 rounded-lg bg-secondary-100 text-secondary-900 flex items-center justify-center flex-shrink-0">
            <i className="ri-folders-line"></i>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-foreground-950">Catégories de vos contenus</h3>
            <p className="text-xs text-foreground-500">
              Calculées depuis vos produits, services et annonces publiés.
            </p>
          </div>
        </div>
        {productCats.length === 0 ? (
          <p className="text-xs text-foreground-400">
            Publiez des contenus classés dans une catégorie pour apparaître dans cette catégorie sur la marketplace.
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {productCats.map((c) => (
              <span
                key={c.id}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 text-[11px] font-medium whitespace-nowrap"
              >
                <i className="ri-price-tag-2-line text-[10px]"></i>
                {c.nom}
              </span>
            ))}
          </div>
        )}
        <p className="mt-3 text-[11px] text-foreground-400 flex items-start gap-2">
          <i className="ri-information-line mt-0.5 flex-shrink-0"></i>
          <span>
            Astuce : vous pouvez classer chaque produit ou annonce dans une catégorie différente
            (dans « Mes produits » / « Mes annonces »). Vous apparaîtrez alors dans chacune de ces catégories.
          </span>
        </p>
      </div>
    </div>
  );
}